// # Chapter Counter Page Component
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../auth/state';
import { db } from '../../../firebase/config';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import SharedHeader from '../../../components/SharedHeader';
import DashboardFooter from '../../../components/DashboardFooter';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import './counter2_page.css';

// # Define mistake types with consistent colors
const MISTAKE_TYPES = [
  { id: 'lackOfAttention', label: 'Lack of Attention', color: '#FF6B6B' },
  { id: 'confusingChoices', label: 'Confusing Choices', color: '#4ECDC4' },
  { id: 'lackOfKnowledge', label: 'Lack of Knowledge', color: '#45B7D1' },
  { id: 'forgottenInfo', label: 'Forgotten Info', color: '#96CEB4' },
  { id: 'wrongInterpretation', label: 'Wrong Interpretation', color: '#C7A9C9' },
  { id: 'notSpecified', label: 'Not Specified', color: '#B8B8B8' }
];

function Counter2Page({ isAdmin }) {
  const { chapterId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [targetUserId, setTargetUserId] = useState(null);
  const [isMentor, setIsMentor] = useState(false);
  const [activeTab, setActiveTab] = useState('pie');
  const [mistakeData, setMistakeData] = useState({
    mistakes: MISTAKE_TYPES.reduce((acc, type) => ({ ...acc, [type.id]: 0 }), {}),
    totalMistakes: 0,
    lastUpdated: null
  });
  const [loading, setLoading] = useState(true);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [previousState, setPreviousState] = useState(null);

  // # Get the correct user ID (mentee's ID when mentor is viewing)
  useEffect(() => {
    const menteeId = sessionStorage.getItem('currentViewingMenteeId');
    setTargetUserId(menteeId || user?.uid);
    setIsMentor(!!menteeId);
  }, [user?.uid]);

  // # Load mistake data from Firestore
  useEffect(() => {
    const loadMistakeData = async () => {
      if (!targetUserId || !chapterId) {
        setLoading(false);
        return;
      }

      try {
        // # Get the user's s2_mentee document
        const menteeRef = doc(db, 's2_mentee', targetUserId);
        const menteeDoc = await getDoc(menteeRef);

        if (menteeDoc.exists()) {
          // # Get mistake counter data for the specific chapter
          const chapterData = menteeDoc.data()?.mistakeCounter?.chapters?.[chapterId];
          
          if (chapterData) {
            setMistakeData({
              mistakes: chapterData.mistakes || MISTAKE_TYPES.reduce((acc, type) => ({ ...acc, [type.id]: 0 }), {}),
              totalMistakes: chapterData.totalMistakes || 0,
              lastUpdated: chapterData.lastUpdated || null
            });
          }
        }
      } catch (error) {
        console.error('Error loading mistake data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadMistakeData();
  }, [targetUserId, chapterId]);

  // # Handle mistake increment
  const handleMistakeIncrement = async (typeId) => {
    if (!targetUserId || !chapterId) return;

    try {
      // # Save current state for undo
      setPreviousState({ ...mistakeData });

      // # Update local state immediately for better UX
      const newMistakes = {
        ...mistakeData.mistakes,
        [typeId]: (mistakeData.mistakes[typeId] || 0) + 1
      };

      const newMistakeData = {
        mistakes: newMistakes,
        totalMistakes: mistakeData.totalMistakes + 1,
        lastUpdated: new Date().toISOString()
      };

      setMistakeData(newMistakeData);

      // # Update Firestore
      const menteeRef = doc(db, 's2_mentee', targetUserId);
      await updateDoc(menteeRef, {
        [`mistakeCounter.chapters.${chapterId}`]: newMistakeData,
        'mistakeCounter.metadata.lastUpdated': new Date().toISOString(),
        'metadata.lastEditedAt': new Date().toISOString()
      });

    } catch (error) {
      console.error('Error incrementing mistake:', error);
      // # Revert local state on error
      setMistakeData(prevData => ({...prevData}));
    }
  };

  // # Handle undo
  const handleUndo = async () => {
    if (!previousState || !targetUserId || !chapterId) return;

    try {
      // # Update Firestore with previous state
      const menteeRef = doc(db, 's2_mentee', targetUserId);
      await updateDoc(menteeRef, {
        [`mistakeCounter.chapters.${chapterId}`]: previousState,
        'mistakeCounter.metadata.lastUpdated': new Date().toISOString(),
        'metadata.lastEditedAt': new Date().toISOString()
      });

      // # Update local state
      setMistakeData(previousState);
      setPreviousState(null);
    } catch (error) {
      console.error('Error undoing action:', error);
    }
  };

  // # Handle clear all
  const handleClear = async () => {
    if (!targetUserId || !chapterId) return;

    try {
      // # Save current state for undo
      setPreviousState({ ...mistakeData });

      // # Create empty state
      const emptyState = {
        mistakes: MISTAKE_TYPES.reduce((acc, type) => ({ ...acc, [type.id]: 0 }), {}),
        totalMistakes: 0,
        lastUpdated: new Date().toISOString()
      };

      // # Update Firestore
      const menteeRef = doc(db, 's2_mentee', targetUserId);
      await updateDoc(menteeRef, {
        [`mistakeCounter.chapters.${chapterId}`]: emptyState,
        'mistakeCounter.metadata.lastUpdated': new Date().toISOString(),
        'metadata.lastEditedAt': new Date().toISOString()
      });

      // # Update local state
      setMistakeData(emptyState);
      setShowClearConfirm(false);
    } catch (error) {
      console.error('Error clearing mistakes:', error);
    }
  };

  // # Prepare chart data
  const chartData = MISTAKE_TYPES.map(type => ({
    name: type.label,
    value: mistakeData.mistakes[type.id] || 0,
    color: type.color
  })).filter(item => item.value > 0);

  return (
    <div className="counter2_page_container">
      <SharedHeader />
      <div className="counter2_page_main">
        <div className="counter2_page_title">
          <h2>Chapter {chapterId} - Mistake Analysis</h2>
          {isMentor && <span className="counter2_page_mentor_pill">Mentor</span>}
          <div className="counter2_page_total">
            Total Mistakes: {mistakeData.totalMistakes}
          </div>
        </div>
        
        <div className="counter2_page_content">
          {loading ? (
            <div className="counter2_page_loading">Loading mistake data...</div>
          ) : (
            <>
              {/* # Left side - Mistake Type Buttons */}
              <div className="counter2_page_buttons_container">
                <div className="counter2_page_buttons">
                  {MISTAKE_TYPES.map(type => (
                    <button
                      key={type.id}
                      className="counter2_page_button"
                      onClick={() => handleMistakeIncrement(type.id)}
                      style={{ borderColor: type.color }}
                    >
                      {type.label}
                      <span className="counter2_page_value">
                        {mistakeData.mistakes[type.id] || 0}
                      </span>
                    </button>
                  ))}
                </div>
                <div className="counter2_page_actions">
                  <button 
                    className="counter2_page_undo" 
                    onClick={handleUndo}
                    disabled={!previousState}
                  >
                    Undo Last Action
                  </button>
                  <button 
                    className="counter2_page_clear"
                    onClick={() => setShowClearConfirm(true)}
                  >
                    Clear All Mistakes
                  </button>
                </div>
              </div>

              {/* # Right side - Charts */}
              <div className="counter2_page_charts_container">
                <div className="counter2_page_tabs">
                  <button 
                    className={`counter2_page_tab ${activeTab === 'pie' ? 'active' : ''}`}
                    onClick={() => setActiveTab('pie')}
                  >
                    Pie Chart
                  </button>
                  <button 
                    className={`counter2_page_tab ${activeTab === 'bar' ? 'active' : ''}`}
                    onClick={() => setActiveTab('bar')}
                  >
                    Bar Chart
                  </button>
                </div>

                <div className="counter2_page_chart">
                  <ResponsiveContainer width="100%" height={400}>
                    {activeTab === 'pie' ? (
                      <PieChart>
                        <Pie
                          data={chartData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={120}
                          label={({ name, value }) => `${name}: ${value}`}
                        >
                          {chartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip 
                          contentStyle={{ backgroundColor: 'white', borderRadius: '8px' }}
                          itemStyle={{ color: '#333' }}
                        />
                        <Legend 
                          verticalAlign="bottom" 
                          height={36}
                          formatter={(value, entry) => (
                            <span style={{ color: entry.color }}>{value}</span>
                          )}
                        />
                      </PieChart>
                    ) : (
                      <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip 
                          contentStyle={{ backgroundColor: 'white', borderRadius: '8px' }}
                          itemStyle={{ color: '#333' }}
                        />
                        <Legend 
                          verticalAlign="top" 
                          height={36}
                          formatter={(value, entry) => (
                            <span style={{ color: entry.payload.fill }}>{value}</span>
                          )}
                        />
                        <Bar dataKey="value" name="Count">
                          {chartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Bar>
                      </BarChart>
                    )}
                  </ResponsiveContainer>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* # Clear Confirmation Dialog */}
      {showClearConfirm && (
        <div className="counter2_page_modal">
          <div className="counter2_page_modal_content">
            <h3>Clear All Mistakes?</h3>
            <p>Are you sure you want to clear all mistakes for this chapter? This action can be undone.</p>
            <div className="counter2_page_modal_actions">
              <button onClick={() => setShowClearConfirm(false)}>Cancel</button>
              <button onClick={handleClear} className="counter2_page_modal_confirm">
                Clear All
              </button>
            </div>
          </div>
        </div>
      )}

      <DashboardFooter />
    </div>
  );
}

export default Counter2Page;
